import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useParams
} from 'react-router-dom';

const colours = {
  brown: '#3B2A21',
  green: '#556B2F',
  cream: '#F2EFE7',
  gold: '#A8893D',
  red: '#8F2E24',
  border: '#E8E0D4',
  muted: '#6B7280'
};

async function publicRequest(
  path,
  options = {}
) {
  const response = await fetch(
    `/api/public/documents${path}`,
    {
      headers: {
        'Content-Type':
          'application/json',
        ...options.headers
      },
      ...options,
      body: options.body
        ? JSON.stringify(options.body)
        : undefined
    }
  );

  let data;

  try {
    data = await response.json();
  } catch {
    data = {
      error:
        'The server returned an invalid response'
    };
  }

  if (!response.ok) {
    const error = new Error(
      data.error ||
      'The document request failed'
    );

    error.status = response.status;
    error.code = data.code;
    error.data = data;

    throw error;
  }

  return data;
}

function formatDate(value) {
  if (!value) {
    return 'Not recorded';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString(
    'en-NG',
    {
      dateStyle: 'long',
      timeStyle: 'short'
    }
  );
}

function optionValue(option) {
  if (
    option &&
    typeof option === 'object'
  ) {
    return (
      option.value ||
      option.label ||
      ''
    );
  }

  return String(option || '');
}

function optionLabel(option) {
  if (
    option &&
    typeof option === 'object'
  ) {
    return (
      option.label ||
      option.value ||
      ''
    );
  }

  return String(option || '');
}

function FieldInput({
  field,
  value,
  onChange
}) {
  const type = String(
    field.input_type || 'text'
  ).toLowerCase();

  const options =
    Array.isArray(field.options)
      ? field.options
      : [];

  if (type === 'signature') {
    return (
      <div
        style={{
          background: '#F9F8F5',
          border:
            `1px solid ${colours.border}`,
          borderRadius: 8,
          padding: 12,
          color: colours.muted,
          fontSize: 12
        }}
      >
        Your typed full name below will
        serve as your electronic signature.
      </div>
    );
  }

  if (
    type === 'select' ||
    type === 'radio'
  ) {
    return (
      <select
        className="form-control"
        value={value || ''}
        onChange={event =>
          onChange(event.target.value)
        }
        required={field.is_required}
      >
        <option value="">
          Select an option
        </option>

        {options.map(
          (option, index) => (
            <option
              key={`${optionValue(option)}-${index}`}
              value={optionValue(option)}
            >
              {optionLabel(option)}
            </option>
          )
        )}
      </select>
    );
  }

  if (
    type === 'checkbox' ||
    type === 'boolean'
  ) {
    return (
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: 'pointer'
        }}
      >
        <input
          type="checkbox"
          checked={value === true}
          onChange={event =>
            onChange(
              event.target.checked
            )
          }
          style={{
            width: 18,
            height: 18,
            accentColor:
              colours.green,
            marginTop: 1
          }}
        />

        <span
          style={{
            color: colours.brown,
            fontSize: 13,
            lineHeight: 1.5
          }}
        >
          Confirm
        </span>
      </label>
    );
  }

  if (
    type === 'textarea' ||
    type === 'long_text'
  ) {
    return (
      <textarea
        className="form-control"
        rows={5}
        value={value || ''}
        onChange={event =>
          onChange(event.target.value)
        }
        required={field.is_required}
      />
    );
  }

  const htmlType = {
    number: 'number',
    currency: 'number',
    date: 'date',
    email: 'email',
    tel: 'tel',
    phone: 'tel'
  }[type] || 'text';

  return (
    <input
      type={htmlType}
      className="form-control"
      value={value || ''}
      onChange={event =>
        onChange(event.target.value)
      }
      required={field.is_required}
      step={
        type === 'currency'
          ? '0.01'
          : undefined
      }
    />
  );
}

export default function DocumentResponse() {
  const {
    token
  } = useParams();

  const [
    document,
    setDocument
  ] = useState(null);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    submitting,
    setSubmitting
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    terminalCode,
    setTerminalCode
  ] = useState('');

  const [
    completed,
    setCompleted
  ] = useState(null);

  const [
    decision,
    setDecision
  ] = useState('accept');

  const [
    typedName,
    setTypedName
  ] = useState('');

  const [
    certified,
    setCertified
  ] = useState(false);

  const [
    declineReason,
    setDeclineReason
  ] = useState('');

  const [
    fields,
    setFields
  ] = useState({});

  useEffect(() => {
    let active = true;

    async function loadDocument() {
      setLoading(true);
      setError('');

      try {
        const result =
          await publicRequest(
            `/${encodeURIComponent(token)}`
          );

        if (active) {
          setDocument(result.data);
        }
      } catch (loadError) {
        if (active) {
          setError(
            loadError.message ||
            'Document could not be loaded'
          );

          setTerminalCode(
            loadError.code || ''
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDocument();

    return () => {
      active = false;
    };
  }, [token]);

  const recipientFields = useMemo(
    () =>
      Array.isArray(document?.fields)
        ? document.fields
        : [],
    [document]
  );

  const updateField = (
    key,
    value
  ) => {
    setFields(current => ({
      ...current,
      [key]: value
    }));
  };

  const submitResponse =
    async event => {
      event.preventDefault();

      if (
        typedName.trim() !==
        String(
          document?.recipient_name || ''
        ).trim()
      ) {
        setError(
          'Your typed name must exactly match the recipient name displayed above'
        );

        return;
      }

      if (!certified) {
        setError(
          'You must confirm the response declaration'
        );

        return;
      }

      if (
        decision === 'decline' &&
        declineReason.trim().length < 5
      ) {
        setError(
          'Enter a clear reason for declining'
        );

        return;
      }

      const action =
        decision === 'accept'
          ? 'accept'
          : 'decline';

      const confirmed =
        window.confirm(
          action === 'accept'
            ? 'Submit your acceptance? This response cannot be changed afterward.'
            : 'Submit your decision to decline? This response cannot be changed afterward.'
        );

      if (!confirmed) {
        return;
      }

      setSubmitting(true);
      setError('');

      try {
        const result =
          await publicRequest(
            `/${encodeURIComponent(token)}/respond`,
            {
              method: 'POST',
              body: {
                decision,
                typed_name:
                  typedName.trim(),
                certified: true,
                fields,
                decline_reason:
                  decision === 'decline'
                    ? declineReason.trim()
                    : null
              }
            }
          );

        setCompleted(result.data);
      } catch (submitError) {
        if (
          submitError.code ===
          'DOCUMENT_ALREADY_RESPONDED'
        ) {
          setCompleted({
            status:
              submitError.data?.status ||
              'completed'
          });

          return;
        }

        if (
          submitError.code ===
          'DOCUMENT_RESPONSE_VALIDATION_FAILED'
        ) {
          const fieldErrors =
            Array.isArray(
              submitError.data?.fields
            )
              ? submitError.data.fields
                  .map(item =>
                    `${item.key}: ${item.error}`
                  )
                  .join('; ')
              : '';

          setError(
            fieldErrors ||
            submitError.message
          );

          return;
        }

        setError(
          submitError.message ||
          'Your response could not be recorded'
        );
      } finally {
        setSubmitting(false);
      }
    };

  const pageStyle = {
    minHeight: '100vh',
    background:
      'linear-gradient(135deg,#F2EFE7 0%,#FFFFFF 60%,#F5F0E5 100%)',
    padding: '25px 14px 60px'
  };

  const containerStyle = {
    maxWidth: 900,
    margin: '0 auto'
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div
            style={{
              background: '#fff',
              border:
                `1px solid ${colours.border}`,
              borderRadius: 14,
              padding: 30,
              textAlign: 'center'
            }}
          >
            <h1
              style={{
                color: colours.brown,
                fontFamily:
                  'Cormorant Garamond, serif'
              }}
            >
              Document unavailable
            </h1>

            <p
              style={{
                color: colours.muted,
                lineHeight: 1.6
              }}
            >
              {error}
            </p>

            {terminalCode && (
              <div
                style={{
                  color: colours.muted,
                  fontSize: 11,
                  marginTop: 15
                }}
              >
                Reference: {terminalCode}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (completed) {
    const accepted =
      completed.status === 'accepted';

    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div
            style={{
              background: '#fff',
              border:
                `1px solid ${colours.border}`,
              borderRadius: 14,
              padding: 35,
              textAlign: 'center'
            }}
          >
            <div
              style={{
                width: 62,
                height: 62,
                borderRadius: '50%',
                background: accepted
                  ? '#DCFCE7'
                  : '#FEE2E2',
                color: accepted
                  ? '#166534'
                  : '#991B1B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 18px',
                fontSize: 28,
                fontWeight: 700
              }}
            >
              {accepted ? '✓' : '!'}
            </div>

            <h1
              style={{
                color: colours.brown,
                fontFamily:
                  'Cormorant Garamond, serif',
                marginBottom: 10
              }}
            >
              {accepted
                ? 'Document accepted'
                : 'Response recorded'}
            </h1>

            <p
              style={{
                color: colours.muted,
                lineHeight: 1.6
              }}
            >
              Your response to{' '}
              <strong>
                {document.title}
              </strong>{' '}
              has been securely recorded.
            </p>

            {completed.audit_reference && (
              <div
                style={{
                  marginTop: 20,
                  padding: 12,
                  background: '#F9F8F5',
                  borderRadius: 8,
                  color: colours.muted,
                  fontFamily: 'monospace',
                  fontSize: 12
                }}
              >
                Audit reference:{' '}
                {completed.audit_reference}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const alreadyResponded =
    ['accepted', 'declined'].includes(
      document.status
    );

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <header
          style={{
            background: colours.brown,
            borderRadius: 14,
            padding: '22px 25px',
            marginBottom: 18,
            color: colours.cream
          }}
        >
          <div
            style={{
              color: colours.gold,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '.1em',
              marginBottom: 5
            }}
          >
            Bloans Creations Ltd.
          </div>

          <h1
            style={{
              fontFamily:
                'Cormorant Garamond, serif',
              fontSize: '1.7rem',
              margin: 0
            }}
          >
            Secure Employment Document
          </h1>
        </header>

        {error && (
          <div className="alert alert-error mb-md">
            {error}
          </div>
        )}

        <section
          style={{
            background: '#fff',
            border:
              `1px solid ${colours.border}`,
            borderRadius: 12,
            padding: 18,
            marginBottom: 18
          }}
        >
          <h2
            style={{
              color: colours.brown,
              margin: '0 0 6px',
              fontSize: 20
            }}
          >
            {document.title}
          </h2>

          <div
            style={{
              color: colours.muted,
              fontSize: 12,
              lineHeight: 1.8
            }}
          >
            <div>
              Recipient:{' '}
              <strong>
                {document.recipient_name}
              </strong>
            </div>

            <div>
              Reference:{' '}
              <span
                style={{
                  fontFamily: 'monospace'
                }}
              >
                {document.document_ref}
              </span>
            </div>

            <div>
              Version:{' '}
              {document.template_version}
            </div>

            <div>
              Issued:{' '}
              {formatDate(
                document.issued_at
              )}
            </div>

            <div>
              Link expires:{' '}
              {formatDate(
                document.expires_at
              )}
            </div>
          </div>
        </section>

        <section
          style={{
            background: '#fff',
            border:
              `1px solid ${colours.border}`,
            borderRadius: 12,
            padding: 25,
            marginBottom: 18,
            overflowX: 'auto'
          }}
        >
          <div
            style={{
              color: colours.brown,
              fontFamily:
                'Georgia, serif',
              fontSize: 14,
              lineHeight: 1.8
            }}
            dangerouslySetInnerHTML={{
              __html:
                document.content || ''
            }}
          />
        </section>

        {alreadyResponded ? (
          <section
            style={{
              background: '#fff',
              border:
                `1px solid ${colours.border}`,
              borderRadius: 12,
              padding: 25,
              textAlign: 'center'
            }}
          >
            <h2
              style={{
                color: colours.brown
              }}
            >
              Response already recorded
            </h2>

            <p
              style={{
                color: colours.muted
              }}
            >
              This document was already{' '}
              <strong>
                {document.status}
              </strong>
              .
            </p>
          </section>
        ) : (
          <form
            onSubmit={submitResponse}
            style={{
              background: '#fff',
              border:
                `1px solid ${colours.border}`,
              borderRadius: 12,
              padding: 22
            }}
          >
            <h2
              style={{
                color: colours.brown,
                marginTop: 0
              }}
            >
              Your response
            </h2>

            {recipientFields.map(field => (
              <div
                className="form-group"
                key={field.key}
              >
                <label className="form-label">
                  {field.field_label ||
                    field.label ||
                    field.key}

                  {field.is_required
                    ? ' *'
                    : ''}
                </label>

                <FieldInput
                  field={field}
                  value={
                    fields[field.key]
                  }
                  onChange={value =>
                    updateField(
                      field.key,
                      value
                    )
                  }
                />

                {field.help_text && (
                  <div
                    style={{
                      color:
                        colours.muted,
                      fontSize: 11,
                      marginTop: 5
                    }}
                  >
                    {field.help_text}
                  </div>
                )}
              </div>
            ))}

            <div className="form-group">
              <label className="form-label">
                Decision *
              </label>

              <div
                style={{
                  display: 'flex',
                  gap: 15,
                  flexWrap: 'wrap'
                }}
              >
                <label
                  style={{
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="radio"
                    name="decision"
                    value="accept"
                    checked={
                      decision === 'accept'
                    }
                    onChange={() =>
                      setDecision('accept')
                    }
                    style={{
                      accentColor:
                        colours.green
                    }}
                  />{' '}
                  Accept document
                </label>

                <label
                  style={{
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="radio"
                    name="decision"
                    value="decline"
                    checked={
                      decision === 'decline'
                    }
                    onChange={() =>
                      setDecision('decline')
                    }
                    style={{
                      accentColor:
                        colours.red
                    }}
                  />{' '}
                  Decline document
                </label>
              </div>
            </div>

            {decision === 'decline' && (
              <div className="form-group">
                <label className="form-label">
                  Reason for declining *
                </label>

                <textarea
                  className="form-control"
                  rows={4}
                  value={declineReason}
                  onChange={event =>
                    setDeclineReason(
                      event.target.value
                    )
                  }
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                Type your full name exactly
                as displayed above *
              </label>

              <input
                type="text"
                className="form-control"
                value={typedName}
                onChange={event =>
                  setTypedName(
                    event.target.value
                  )
                }
                autoComplete="name"
                required
              />
            </div>

            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                marginBottom: 20,
                cursor: 'pointer',
                color: colours.brown,
                fontSize: 13,
                lineHeight: 1.5
              }}
            >
              <input
                type="checkbox"
                checked={certified}
                onChange={event =>
                  setCertified(
                    event.target.checked
                  )
                }
                style={{
                  width: 18,
                  height: 18,
                  marginTop: 2,
                  accentColor:
                    colours.green
                }}
              />

              <span>
                I confirm that I reviewed
                this document and that my
                response and information are
                accurate.
              </span>
            </label>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{
                width: '100%',
                padding: 13
              }}
            >
              {submitting
                ? 'Recording response...'
                : decision === 'accept'
                  ? 'Accept and Sign'
                  : 'Submit Decline Decision'}
            </button>
          </form>
        )}

        <footer
          style={{
            textAlign: 'center',
            color: colours.muted,
            fontSize: 10,
            marginTop: 20
          }}
        >
          This secure link is unique to the
          named recipient. Do not forward it.
        </footer>
      </div>
    </div>
  );
}
